import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Inbox, Check, ChevronRight, X } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationsContext';

function relTime(s) {
  if (!s) return '';
  const d = new Date(s); const diff = Date.now() - d.getTime();
  const m = 60_000, h = 60 * m, day = 24 * h;
  if (diff < m) return 'just now';
  if (diff < h) return `${Math.floor(diff / m)}m ago`;
  if (diff < day) return `${Math.floor(diff / h)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function groupItems(items) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const weekStart = new Date(today); weekStart.setDate(today.getDate() - 6);
  const groups = { Today: [], 'Earlier this week': [], Older: [] };
  items.forEach(n => {
    const d = new Date(n.created_at);
    if (d >= today) groups.Today.push(n);
    else if (d >= weekStart) groups['Earlier this week'].push(n);
    else groups.Older.push(n);
  });
  return groups;
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const { unreadCount, recent, markRead, markAllRead, displayFor, routeFor } = useNotifications();
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClick = (n) => {
    if (!n.is_read && !n.read) markRead(n.id);
    setOpen(false);
    const route = routeFor(n);
    if (route) navigate(route);
  };

  const groups = groupItems(recent);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(v => !v)}
        data-testid="ops-topbar-notifications"
        title="Notifications"
        className="relative h-8 w-8 grid place-items-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 border border-transparent hover:border-slate-200 transition-colors"
      >
        <Bell size={14} />
        {unreadCount > 0 && (
          <span data-testid="notif-unread-badge" className="ops-pill absolute -top-1 -right-1 bg-slate-900 text-white border-slate-900 px-1.5 py-0 leading-none min-w-[18px] justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={popRef}
          data-testid="notifications-panel"
          className="ops-shell absolute right-0 mt-2 w-[360px] bg-white border border-slate-200 rounded-lg shadow-md z-40 max-h-[70vh] flex flex-col"
        >
          <header className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-slate-900">Notifications</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={markAllRead}
                disabled={unreadCount === 0}
                data-testid="notif-mark-all-read"
                className="text-[11px] text-slate-500 hover:text-slate-900 disabled:opacity-40 inline-flex items-center gap-1"
              >
                <Check size={11} /> Mark all read
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto">
            {recent.length === 0 ? (
              <div className="text-center py-10 px-6">
                <Inbox size={20} className="mx-auto text-slate-300 mb-2" />
                <p className="text-[13px] text-slate-700">You're all caught up</p>
              </div>
            ) : (
              Object.entries(groups).filter(([, items]) => items.length > 0).map(([label, items]) => (
                <section key={label} className="py-1">
                  <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400 px-3 pt-2 pb-1">{label}</p>
                  <ul>
                    {items.map(n => {
                      const d = displayFor(n);
                      const unread = !(n.is_read || n.read);
                      return (
                        <li key={n.id}>
                          <button
                            onClick={() => handleClick(n)}
                            data-testid={`notif-item-${n.id}`}
                            className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-slate-50 transition-colors ${unread ? 'bg-slate-50/60' : ''}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${unread ? 'bg-slate-900' : 'bg-transparent border border-slate-200'}`} />
                            <span className="flex-1 min-w-0">
                              <p className="text-[13px] text-slate-900 truncate leading-tight">{d.title}</p>
                              {d.body && <p className="text-[12px] text-slate-500 truncate leading-tight mt-0.5">{d.body}</p>}
                              <p className="text-[11px] text-slate-400 mt-1">{relTime(n.created_at)}</p>
                            </span>
                            <ChevronRight size={12} className="text-slate-300 mt-1 shrink-0" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))
            )}
          </div>

          <footer className="px-3 py-2 border-t border-slate-200 flex items-center justify-between">
            <button
              onClick={() => { setOpen(false); navigate('/ops/notifications'); }}
              data-testid="notif-view-all"
              className="text-[12px] text-slate-700 hover:text-slate-900 hover:underline"
            >
              View all notifications
            </button>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-700 rounded p-1 transition-colors"
              aria-label="Close"
            >
              <X size={13} />
            </button>
          </footer>
        </div>
      )}
    </div>
  );
}
